import { Button, Flex, ModalContext, Text } from 'dash-ui';
import { useLocation, useParams } from 'react-router-dom';
import { useTheme } from 'styled-components';
import { useTranslation } from 'react-i18next';
import { useContext, useCallback } from 'react';

import { BulkActionsContext } from 'common/contexts/bulkActionsContext/bulkActionsContext';
import MoveFileToFolderModal from 'containers/properties/PropertyMedia/components/MoveFileToFolderModal/MoveFileToFolderModal';
import { RoutesPath } from 'flow-components/routes/routes';
import { downloadMedia } from 'common/helpers/downloadMedia';

import { MainContainer } from './BulkActionsContainer.styles';
import DeleteMediaFilesModal from './components/DeleteMediaFilesModal/DeleteMediaFilesModal';
import BulkUpdateBrandingModal from './components/BulkUpdateBrandingModal/BulkUpdateBrandingModal';

const BulkActionsContainer = () => {
  const theme = useTheme();
  const { t } = useTranslation(['common', 'pages']);
  const { id: propertyId } = useParams();
  const {
    filter,
    selected: selectedMedia,
    unselected,
    selected,
    isAllSelected,
    resetState,
  } = useContext(BulkActionsContext);
  const { setPayload, toggleModal } = useContext(ModalContext);
  const { pathname } = useLocation();
  const isOpen =
    (isAllSelected || selectedMedia.length > 0) &&
    pathname.includes(RoutesPath.Properties) &&
    pathname.length > RoutesPath.Properties.length;

  const selectedCount = selected.length;

  const onMoveToFolder = useCallback(() => {
    toggleModal(true, 'MoveFileToFolderModal');
    setPayload({
      propertyId,
      mediaIds: selectedMedia,
      filter: isAllSelected ? filter : null,
      excludedIds: isAllSelected ? unselected : null,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedMedia, propertyId, filter, isAllSelected, unselected]);

  const onDownLoad = useCallback(() => {
    downloadMedia(
      selectedMedia,
      isAllSelected && {
        ...filter,
        excludedIds: unselected,
      }
    );
  }, [selectedMedia, filter, isAllSelected, unselected]);

  const onRemoveMedia = useCallback(() => {
    toggleModal(true, 'BulkDeleteMediaModal');
    setPayload({
      selectedCount,
      mediaIds: selectedMedia,
      filter: isAllSelected ? filter : null,
      excludedIds: isAllSelected ? unselected : null,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedMedia, filter, isAllSelected, unselected, selectedCount]);

  const onUpdateBranding = useCallback(() => {
    toggleModal(true, 'BulkUpdateBrandingModal');
    setPayload({
      selectedCount,
      mediaIds: selectedMedia,
      filter: isAllSelected ? filter : null,
      excludedIds: isAllSelected ? unselected : null,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedMedia, filter, isAllSelected, unselected, selectedCount]);

  return (
    <>
      <MoveFileToFolderModal onCompleted={resetState} />
      <DeleteMediaFilesModal onCompleted={resetState} />
      <BulkUpdateBrandingModal onCompleted={resetState} />
      {isOpen && !!selectedCount && (
        <MainContainer
          h={80}
          alignItems="center"
          justifyContent="space-between"
          px={17}
        >
          <Text color={theme.colors.n80}>
            {t('pages:propertyPage.bulkActionsText', {
              quantity: selectedCount,
            })}
          </Text>
          <Flex>
            <Button
              h={40}
              minWidth={167}
              icon="driveFileMove"
              iconProps={{ size: 18 }}
              onClick={onMoveToFolder}
            >
              {t('pages:propertyPage.moveToFolder')}
            </Button>
            <Button
              mx={3}
              h={40}
              minWidth={167}
              icon="edit"
              iconProps={{ size: 18 }}
              onClick={onUpdateBranding}
            >
              {t('pages:propertyPage.updateBranding')}
            </Button>
            <Button
              variation="outline"
              icon="download"
              iconProps={{ size: 18 }}
              h={40}
              minWidth={133}
              onClick={onDownLoad}
            >
              {t('common:labels.download')}
            </Button>
            <Button
              ml={3}
              minWidth={108}
              h={40}
              variation="outline"
              icon="trashSolid"
              iconProps={{ size: 18 }}
              onClick={onRemoveMedia}
            >
              {t('common:labels.delete')}
            </Button>
          </Flex>
        </MainContainer>
      )}
    </>
  );
};

export default BulkActionsContainer;
